import { Form, useForm } from "react-final-form";

import { patchOrganizationUser } from "@/api/endpoints/organization";
import { postTrackingData } from "@/api/endpoints/track";
import { PLAN_TYPE } from "@/constants";
import EditUserModal from "@/containers/EditUserModal";
import { useMessages } from "@/containers/Messages/MessageContext";
import UserSettingFields from "@/containers/UserSettingFields";
import { FEATURE } from "@/features";
import { useOrganization } from "@/hooks/useOrganization";
import useSearchParams from "@/hooks/useSearchParams";
import { useUser } from "@/hooks/useUser";
import { EVENT } from "@/tracking_events";
import { cx } from "@/utils";
import Divider from "@/views/settings/partials/Divider";
import ApiToken from "@/views/settings/profile/ApiToken";
import ChangePassword from "@/views/settings/profile/ChangePassword";
import Licenses from "@/views/settings/profile/Licenses";
import AllEarsTitle from "@components/AllEarsTitle";
import Button from "@components/Button";
import { Feature } from "@components/Feature";
import Rhythm from "@components/Rhythm";
import Typography from "@components/Typography";

import styles from "./ProfileSettings.module.css";
import { useLingui } from "@lingui/react/macro";
import { User } from "@/types/User";

const deleteProfile = false;

type ResetButtonProps = React.ComponentProps<typeof Button>;

const ResetButton: React.FC<ResetButtonProps> = (props) => {
  const { t } = useLingui();
  const form = useForm();

  return (
    <Button onClick={() => form.reset()} variant="tertiary" {...props}>
      {t`Avbryt`}
    </Button>
  );
};

const ProfileSettings = () => {
  const { t } = useLingui();
  const { data: userData } = useUser();
  const user = userData?.ok ? userData.data : null;

  const { data: orgData } = useOrganization();
  const organization = orgData?.ok ? orgData.data : null;
  const [params, setParams] = useSearchParams();
  const { success } = useMessages();
  const { subscriptions = false } = params;

  if (!user || !organization) return null;

  const handleSave = async (_user: User) =>
    patchOrganizationUser({
      user_id: user.id,
      organization_uuid: organization.uuid,
      user: _user,
    }).then(async (response) => {
      if (response.status === 200) {
        await Promise.all([
          success({ title: t`Sparat!`, duration: 1500 }),
          postTrackingData(EVENT.UPDATE_SETTINGS),
        ]);
        // Perform a hard reload if currency or language has changed.
        if (
          user.currency != response.data.currency ||
          user.language != response.data.language
        ) {
          window.location.reload();
        }
      } else {
        return response.data;
      }
    });

  return (
    <>
      <AllEarsTitle title={L.USER_SETTINGS_TITLE} />
      <Rhythm data-testid="user-settings-page">
        <Form
          initialValues={{
            name: user.name,
            email: user.email,
            currency: user.currency,
            language: user.language,
            preferred_mail_delivery_time: user.preferred_mail_delivery_time,
            send_mail_on_weekends: user.send_mail_on_weekends,
            time_zone: user.time_zone,
          }}
          onSubmit={handleSave}
        >
          {({ handleSubmit, pristine, valid, submitting }) => (
            <form onSubmit={handleSubmit}>
              <div className={styles.fields}>
                <UserSettingFields />
                <div className={styles.actions}>
                  <Button
                    data-testid="save-profile-settings"
                    disabled={!valid || pristine || submitting}
                    type="submit"
                  >
                    {L.USER_SETTINGS_SAVE}
                  </Button>
                  <ResetButton disabled={pristine} />
                </div>
              </div>
            </form>
          )}
        </Form>
      </Rhythm>
      {organization?.plan?.type !== PLAN_TYPE.FREE && (
        <>
          <Divider />
          <Typography variant="subtitle-2">{t`Prenumerationer`}</Typography>
          <Typography>{t`Här kan du se och ändra hur ofta du får mejl från dina bevakningar och rapporter.`}</Typography>
          <Button
            variant="secondary"
            onClick={() => setParams({ subscriptions: true })}
          >
            {t`Mina prenumerationer`}
          </Button>
        </>
      )}
      <Divider />
      <Rhythm>
        <Typography variant="subtitle-2">
          {L.USER_SETTINGS_PASSWORD_TITLE}
        </Typography>
        <ChangePassword
          className={cx(styles.fields, styles.password)}
          onSuccess={() => {
            success({
              title: t`Lösenordet uppdaterat`,
            });
          }}
        />

        <Divider />

        <Feature required={FEATURE.HAS_API_ACCESS}>
          <Typography variant="subtitle-2">
            {L.USER_SETTINGS_API_TOKEN_TITLE}
          </Typography>
          <ApiToken className={cx(styles.fields)} />
          <Divider />
        </Feature>

        {deleteProfile && (
          <>
            <Typography variant="subtitle-2">
              {L.USER_SETTINGS_DELETE_TITLE}
            </Typography>
            <Typography>{L.USER_SETTINGS_DELETE_DESCRIPTION}</Typography>
            <Button disabled>{L.USER_SETTINGS_DELETE_CTA}</Button>
            <Divider />
          </>
        )}
        <EditUserModal
          title={t`Mina prenumerationer`}
          hideUserSettings
          onHide={() => setParams({ subscriptions: undefined })}
          show={!!subscriptions}
          user={user}
        />
        <Licenses />
      </Rhythm>
    </>
  );
};

export default ProfileSettings;
